import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { intakeApi, adminDataQualityApi, orderApi, vendorApi, validationFlagApi } from '../api/services';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import LifecycleBadge from '../components/common/LifecycleBadge';
import AssignToUserModal from '../components/modals/AssignToUserModal';
import BatchStatusBreakdown, { getBucket, isOverdue, matchesTrackingMetric } from '../components/intake/BatchStatusBreakdown';
import { addWorkingDaysISO } from '../utils/workingDays';
import { UserPlus, AlertTriangle, ArrowLeft, Loader2, Users2, Calendar, CheckCircle2, Download } from 'lucide-react';

function FlagChip({ children }) {
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 mr-1">
      {children}
    </span>
  );
}

export default function IntakeBatchDetail() {
  const { id } = useParams();
  const { isAdmin, isSuperAdmin, isDispatchUser } = useAuth();
  const [batch, setBatch] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [raising, setRaising] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Stage filter for the orders table — Kalpana asked for quick filtering
  // between Intake / In review / Reships etc when a batch has 80+ rows.
  const [stageFilter, setStageFilter] = useState('all');

  async function load() {
    setLoading(true);
    try {
      const res = await intakeApi.get(id);
      setBatch(res.data.batch);
      setOrders(res.data.orders || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load batch');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function downloadOrders() {
    setDownloading(true);
    try {
      await intakeApi.downloadOrders(id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  // Vendors for the bulk-reassign dropdown. Fetched once per page mount.
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    vendorApi.list().then((res) => {
      setVendors(Array.isArray(res.data) ? res.data : (res.data.vendors || []));
    }).catch(() => { /* silent — bulk reassign just disappears if list missing */ });
  }, []);

  // Duplicate-customer detection inside this batch: build a Map keyed by
  // lower(name)+postcode → array of orders sharing that key. Row renders a
  // "⚠ duplicate — N in batch" badge whenever an entry has >1 order.
  const duplicateKey = (o) =>
    `${(o.customer_name || '').trim().toLowerCase()}::${o.shipping_postcode || ''}`;
  const duplicatesByKey = useMemo(() => {
    const m = new Map();
    for (const o of orders) {
      const k = duplicateKey(o);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(o);
    }
    return m;
  }, [orders]);
  const duplicateGroups = useMemo(
    () => Array.from(duplicatesByKey.values()).filter((g) => g.length > 1),
    [duplicatesByKey]
  );

  // Bulk-defer modal state. Default = 3 working days from today (Kalpana
  // UAT round 3). 'Working day' = any day except Sunday.
  const [deferBulkOpen, setDeferBulkOpen] = useState(false);
  const [deferBulkDate, setDeferBulkDate] = useState(() => addWorkingDaysISO(3));

  async function bulkDefer() {
    if (!deferBulkDate) { toast.error('Pick a date'); return; }
    const deferable = orders.filter((o) => selected.has(o.id));
    if (deferable.length === 0) { toast.error('No rows selected'); return; }
    try {
      const res = await orderApi.bulkDefer({
        orderIds: deferable.map((o) => o.id),
        scheduledDate: deferBulkDate,
      });
      toast.success(`Deferred ${res.data.deferred} order(s) / ${res.data.shipmentsUpdated} shipment(s)`);
      if (res.data.skipped?.length > 0) {
        toast(`${res.data.skipped.length} skipped`, { icon: '⚠️' });
      }
      setDeferBulkOpen(false); setDeferBulkDate(addWorkingDaysISO(3));
      clearSelection();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk defer failed');
    }
  }

  async function deferOne(orderId, scheduledDate) {
    try {
      const res = await orderApi.deferOrder(orderId, { scheduledDate });
      toast.success(`Deferred (${res.data.updated} shipment(s))`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Defer failed');
    }
  }

  // Option A fast path (Kalpana 2026-05-12) — resolve every pending
  // validation flag on this order in one click. For corrections, use the
  // Validation Dashboard's inline edit instead.
  const [busyApproveOrderId, setBusyApproveOrderId] = useState(null);
  async function approveAllValidationFlags(orderId, orderExt) {
    if (!window.confirm(`Approve all pending validation flags on ${orderExt}? Brand/generic/container from MBO will be saved to the Product Mapping master.`)) return;
    setBusyApproveOrderId(orderId);
    try {
      const res = await validationFlagApi.approveAllOnOrder(orderId);
      const { approved = 0, skipped = 0, failed = [] } = res.data || {};
      if (failed.length > 0) {
        toast.error(`Approved ${approved}, ${failed.length} failed (see audit log)`);
      } else if (skipped > 0) {
        toast.success(`Approved ${approved}; ${skipped} flag(s) skipped (need manual review)`);
      } else {
        toast.success(`Approved ${approved} flag(s) — order can now move to review`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Approve all failed');
    } finally {
      setBusyApproveOrderId(null);
    }
  }

  const flaggedForAddress = useMemo(
    () => orders.filter((o) => (o.has_missing_zip || o.has_special_char_address) && o.lifecycle_stage === 'intake'),
    [orders]
  );

  function toggle(orderId) {
    const next = new Set(selected);
    next.has(orderId) ? next.delete(orderId) : next.add(orderId);
    setSelected(next);
  }
  function selectAllFlagged() {
    setSelected(new Set(flaggedForAddress.map((o) => o.id)));
  }
  function clearSelection() { setSelected(new Set()); }

  // Bulk Submit for review: acts on selected orders that are in_review +
  // owned by a dispatch_user (the only state where "submit for review" makes
  // sense). Others get silently skipped by the backend.
  const selectedSubmittable = useMemo(() => {
    return orders.filter((o) =>
      selected.has(o.id) &&
      o.lifecycle_stage === 'in_review' &&
      o.current_owner_team === 'dispatch_user'
    );
  }, [orders, selected]);

  // Bulk Send-back: admin-only, acts on selected orders that are in_review +
  // owned by dispatch_admin (submitted but awaiting review). Those get sent
  // back to the dispatch user for correction.
  const selectedSendbackable = useMemo(() => {
    return orders.filter((o) =>
      selected.has(o.id) &&
      o.lifecycle_stage === 'in_review' &&
      o.current_owner_team === 'dispatch_admin'
    );
  }, [orders, selected]);

  async function bulkSendBack() {
    if (!isAdmin()) return;
    if (selectedSendbackable.length === 0) {
      toast.error('No selected orders are awaiting your review');
      return;
    }
    const note = window.prompt('Reason for sending back (one note applied to all):', '');
    if (note === null) return; // cancelled
    try {
      const res = await orderApi.bulkSendBack({
        orderIds: selectedSendbackable.map((o) => o.id),
        reasonNote: note || null,
      });
      toast.success(`Sent back ${res.data.sentBack} order(s)`);
      if (res.data.skipped?.length > 0) {
        toast(`${res.data.skipped.length} skipped`, { icon: '⚠️' });
      }
      clearSelection();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk send-back failed');
    }
  }

  async function bulkSubmitForReview() {
    if (selectedSubmittable.length === 0) {
      toast.error('No selected orders are in-review on your side');
      return;
    }
    try {
      const res = await orderApi.bulkSubmitForReview({ orderIds: selectedSubmittable.map((o) => o.id) });
      toast.success(`Submitted ${res.data.submitted} order(s) for review`);
      if (res.data.skipped?.length > 0) {
        toast(`${res.data.skipped.length} skipped`, { icon: '⚠️' });
      }
      clearSelection();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk submit failed');
    }
  }

  async function raiseSelectedToOps() {
    if (selected.size === 0) return;
    setRaising(true);
    try {
      const res = await adminDataQualityApi.raiseToOps({ orderIds: [...selected] });
      toast.success(`Raised ${res.data.raised} order(s) to Ops`);
      if (res.data.skipped?.length > 0) {
        toast(`${res.data.skipped.length} skipped`, { icon: '⚠️' });
      }
      clearSelection();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to raise to Ops');
    } finally {
      setRaising(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  if (!batch) return <div className="p-6 text-sm text-gray-500">Batch not found.</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Link to="/intake" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-2">
        <ArrowLeft className="w-3.5 h-3.5" /> All batches
      </Link>

      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{batch.batch_number}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {batch.source_filename || '(no filename)'} · {batch.source_type} ·
            uploaded {batch.created_at && format(new Date(batch.created_at), 'dd MMM yyyy HH:mm')}
          </p>
        </div>
        <div className="flex gap-2">
          {/* Issue #24 — daily records export. Dispatch Users need this, so it
              sits outside the isAdmin() gate; Ops can reach this page too but
              the endpoint rejects them, so don't show them a dead button. */}
          {(isDispatchUser() || isAdmin()) && (
            <button
              onClick={downloadOrders}
              disabled={downloading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              {downloading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Download className="w-4 h-4" />} Download Orders
            </button>
          )}
          {isAdmin() && (
            <button
              onClick={() => setAssignOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <UserPlus className="w-4 h-4" /> Assign to user
            </button>
          )}
        </div>
      </div>

      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          ['Orders', orders.length],
          ['Status', <span className="capitalize" key="s">{batch.status}</span>],
          ['Failed rows', Array.isArray(batch.failed_rows) ? batch.failed_rows.length : 0],
          ['Address-flagged', flaggedForAddress.length],
        ].map(([k, v]) => (
          <div key={k} className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs uppercase text-gray-500">{k}</div>
            <div className="text-lg font-semibold">{v}</div>
          </div>
        ))}
      </dl>

      {flaggedForAddress.length > 0 && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-amber-900">
                {flaggedForAddress.length} order(s) have address issues that Ops should verify.
              </div>
              <div className="text-xs text-amber-800 mt-1">
                Select them in the table below and raise to Ops in one click, or select all flagged.
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={selectAllFlagged}
                  className="px-3 py-1 bg-white border border-amber-300 text-amber-900 rounded text-xs hover:bg-amber-100"
                >
                  Select all flagged
                </button>
                {selected.size > 0 && (
                  <>
                    <button
                      onClick={raiseSelectedToOps} disabled={raising}
                      className="flex items-center gap-1 px-3 py-1 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
                    >
                      {raising && <Loader2 className="w-3 h-3 animate-spin" />}
                      Raise {selected.size} to Ops
                    </button>
                    <button onClick={clearSelection} className="text-xs text-amber-900 underline">
                      Clear
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate-customer notice — surfaces at batch-detail level so the
          admin knows to defer one of them BEFORE it hits dispatch */}
      {duplicateGroups.length > 0 && (
        <div className="mb-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <Users2 className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <div className="text-sm text-red-900 flex-1">
            <strong>{duplicateGroups.length} duplicate customer(s) in this batch.</strong>{' '}
            <span className="text-xs">
              Per the 3–4 day rule, ship these orders on different dates. Tick the rows you want to push out and use{' '}
              <em>Defer to date…</em> — or set a date inline in the rightmost column of the table.
            </span>
            <ul className="mt-1 text-xs space-y-0.5">
              {duplicateGroups.slice(0, 5).map((g, i) => (
                <li key={i}>
                  <strong>{g[0].customer_name}</strong> — {g.length} orders:{' '}
                  {g.map((o) => o.order_id_external).join(', ')}
                </li>
              ))}
              {duplicateGroups.length > 5 && <li>…and {duplicateGroups.length - 5} more.</li>}
            </ul>
          </div>
        </div>
      )}

      {/* Selection toolbar — appears whenever any orders are ticked */}
      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <span className="text-xs text-blue-900"><strong>{selected.size}</strong> selected</span>
          {/* Bulk submit-for-review is the Dispatch User's action — they process
              the order, then hand it up to admin. Admin seeing it was confusing
              Kalpana (why would Mani submit on his own behalf?). Gated to
              non-admins now. */}
          {selectedSubmittable.length > 0 && !isAdmin() && (
            <button
              onClick={bulkSubmitForReview}
              className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700"
            >
              Submit {selectedSubmittable.length} for review
            </button>
          )}
          {isAdmin() && selectedSendbackable.length > 0 && (
            <button
              onClick={bulkSendBack}
              className="px-3 py-1 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700"
            >
              Send back {selectedSendbackable.length}
            </button>
          )}
          <button
            onClick={() => setDeferBulkOpen(true)}
            className="px-3 py-1 bg-white border border-blue-300 text-blue-700 rounded text-xs font-medium hover:bg-blue-50 flex items-center gap-1"
          >
            <Calendar className="w-3 h-3" /> Defer {selected.size} to date…
          </button>
          {/* Bulk reassign vendor — admin only. Pulls the vendor list once
              into the dropdown; on selection, calls the bulk endpoint and
              reloads. */}
          {isAdmin() && (
            <select
              value=""
              onChange={async (e) => {
                if (!e.target.value) return;
                const vendorId = e.target.value;
                const vendor = vendors.find((v) => v.id === vendorId);
                if (!window.confirm(`Reassign ${selected.size} order(s) to ${vendor?.name || 'this vendor'}?`)) {
                  e.target.value = ''; return;
                }
                try {
                  const res = await orderApi.bulkReassignVendor({
                    orderIds: [...selected],
                    vendorId,
                  });
                  toast.success(`Reassigned ${res.data.ordersUpdated} order(s) (${res.data.shipmentsUpdated} shipment(s)) to ${res.data.vendorName}`);
                  clearSelection();
                  load();
                } catch (err) {
                  toast.error(err.response?.data?.error || 'Reassign failed');
                }
                e.target.value = '';
              }}
              className="px-3 py-1 bg-white border border-blue-300 text-blue-700 rounded text-xs font-medium hover:bg-blue-50"
            >
              <option value="" disabled>Reassign {selected.size} to vendor…</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}{v.is_default ? ' ★' : ''}
                </option>
              ))}
            </select>
          )}
          <button onClick={clearSelection} className="text-xs text-blue-900 underline ml-auto">Clear</button>
        </div>
      )}

      {/* Grouped status breakdown — prominent summary so admin can see at a
          glance where the orders in this batch currently sit (with ops /
          with vendor / closed etc.) and which are overdue. Clicking a card
          sets a bucket:* filter that the table below honours. */}
      <BatchStatusBreakdown orders={orders} stageFilter={stageFilter} setStageFilter={setStageFilter} />

      {/* Stage filter pills — finer per-stage filter under the breakdown.
          Per Kalpana UAT round 3 (H2): pills are derived dynamically from
          the stages actually present in this batch. Previously the list was
          hardcoded and could include stages that didn't exist on any order
          (e.g. "Awaiting approval"); a stage that wasn't in the hardcoded
          list also wouldn't show even if orders were in it. Now: one pill
          per real stage in the batch, label from STAGE_LABELS. */}
      {(() => {
        const STAGE_LABELS = {
          intake:                          'Intake',
          in_review:                       'In review',
          reship_unlinked:                 'Reship — needs linking',
          data_incomplete:                 'Data incomplete',
          blocked_troublesome_customer:    'Blocked',
          needs_attention:                 'Needs attention',
          with_vendor:                     'With vendor',
          ready_for_dispatch:              'Ready',
          in_transit:                      'In transit',
          on_hold:                         'On hold',
          completed:                       'Completed',
          cancelled:                       'Cancelled',
          delivered_unpaid:                'Delivered — unpaid',
        };
        const counts = orders.reduce((a, o) => {
          a[o.lifecycle_stage] = (a[o.lifecycle_stage] || 0) + 1;
          return a;
        }, {});
        // Stable display order — match STAGE_LABELS key order, then any
        // unknown stages picked up from data appended at the end.
        const presentStages = Object.keys(STAGE_LABELS).filter((k) => counts[k]);
        for (const k of Object.keys(counts)) {
          if (!STAGE_LABELS[k] && !presentStages.includes(k)) presentStages.push(k);
        }
        const FILTERS = [{ key: 'all', label: 'All', count: orders.length }]
          .concat(presentStages.map((k) => ({
            key: k,
            label: STAGE_LABELS[k] || k,
            count: counts[k],
          })));
        return (
          <div className="mb-3 flex flex-wrap gap-2">
            {FILTERS.map((f) => {
              const active = stageFilter === f.key;
              return (
                <button key={f.key} onClick={() => setStageFilter(f.key)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          active
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}>
                  {f.label} <span className="opacity-70">({f.count})</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Orders table — only rows matching stageFilter render */}
      {(() => { /* expose visibleOrders to the rendering below */ })()}
      {(function renderTable() {
        // stageFilter can be one of:
        //   'all'                — no filter
        //   'bucket:<key>'       — lifecycle bucket (top cards)
        //   'overdue:<key>'      — overdue chip within a bucket card
        //   'tracking:<key>'     — tracking metric (TN assigned/missing, delivered, ...)
        //   '<lifecycle_stage>'  — per-stage pill
        const visibleOrders = stageFilter === 'all'
          ? orders
          : stageFilter.startsWith('bucket:')
            ? orders.filter((o) => getBucket(o) === stageFilter.slice(7))
            : stageFilter.startsWith('overdue:')
              ? orders.filter((o) => getBucket(o) === stageFilter.slice(8) && isOverdue(o))
              : stageFilter.startsWith('tracking:')
                ? orders.filter((o) => matchesTrackingMetric(o, stageFilter.slice(9)))
                : orders.filter((o) => o.lifecycle_stage === stageFilter);
        // Inner helper to compute eligibility for header select-all
        const isSelectable = (o) =>
          ((o.has_missing_zip || o.has_special_char_address) && o.lifecycle_stage === 'intake') ||
          (o.lifecycle_stage === 'in_review' && o.current_owner_team === 'dispatch_user') ||
          (isAdmin() && o.lifecycle_stage === 'in_review' && o.current_owner_team === 'dispatch_admin') ||
          (o.lifecycle_stage === 'intake');
        const selectableVisible = visibleOrders.filter(isSelectable);
        const allSelected = selectableVisible.length > 0 && selectableVisible.every((o) => selected.has(o.id));
        const anySelected = selectableVisible.some((o) => selected.has(o.id));
        const toggleAllVisible = () => {
          const next = new Set(selected);
          if (allSelected) selectableVisible.forEach((o) => next.delete(o.id));
          else selectableVisible.forEach((o) => next.add(o.id));
          setSelected(next);
        };
        return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="w-8 px-3 py-3">
                {selectableVisible.length > 0 && (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = anySelected && !allSelected; }}
                    onChange={toggleAllVisible}
                    title={allSelected ? 'Clear all visible' : 'Select all visible eligible'}
                  />
                )}
              </th>
              <th className="text-left px-4 py-3">Order ID</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-left px-4 py-3">Owner</th>
              <th className="text-left px-4 py-3">Flags</th>
              {isSuperAdmin() && <th className="text-right px-4 py-3">Total</th>}
              <th className="text-right px-4 py-3">Defer to</th>
            </tr>
          </thead>
          <tbody>
            {visibleOrders.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                  No orders in this filter.
                </td>
              </tr>
            )}
            {visibleOrders.map((o) => {
              // Checkbox shows if any bulk action can act on this row:
              //  (a) flagged intake address → Raise to Ops
              //  (b) in_review owned by dispatch_user → Submit for review
              //  (c) in_review owned by dispatch_admin → Send back (admin only)
              //  (d) any intake-stage order (Kalpana Q: bulk-defer duplicates)
              const canSelect =
                ((o.has_missing_zip || o.has_special_char_address) && o.lifecycle_stage === 'intake') ||
                (o.lifecycle_stage === 'in_review' && o.current_owner_team === 'dispatch_user') ||
                (isAdmin() && o.lifecycle_stage === 'in_review' && o.current_owner_team === 'dispatch_admin') ||
                (o.lifecycle_stage === 'intake');
              const dupCount = (duplicatesByKey.get(duplicateKey(o)) || []).length;
              const isDuplicate = dupCount > 1;
              return (
                <tr key={o.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-3">
                    {canSelect && (
                      <input
                        type="checkbox"
                        checked={selected.has(o.id)}
                        onChange={() => toggle(o.id)}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-blue-600">
                    <Link to={`/orders/${o.id}`}>{o.order_id_external}</Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span>{o.customer_name}</span>
                      {isDuplicate && (
                        <span title={`This customer has ${dupCount} orders in this batch`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-800">
                          <Users2 className="w-3 h-3" /> {dupCount} in batch
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3"><LifecycleBadge stage={o.lifecycle_stage} ownerTeam={o.current_owner_team} hasReship={o.has_reship} /></td>
                  <td className="px-4 py-3 text-gray-600">{o.current_owner_team}</td>
                  <td className="px-4 py-3">
                    {o.has_virtual_product && <FlagChip>virtual product</FlagChip>}
                    {o.has_missing_zip && <FlagChip>missing zip</FlagChip>}
                    {o.has_special_char_address && <FlagChip>odd address</FlagChip>}
                    {o.is_reship && <FlagChip>reship</FlagChip>}
                  </td>
                  {isSuperAdmin() && (
                    <td className="px-4 py-3 text-right">
                      {o.total_amount != null ? `$${Number(o.total_amount).toFixed(2)}` : '—'}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    {o.lifecycle_stage === 'intake' && (
                      <input
                        type="date"
                        // Show the earliest pending shipment's scheduled_date
                        // so the admin sees the current ship date at a glance.
                        // `key` forces React to re-render with the fresh date
                        // after a defer lands; defaultValue alone would stick.
                        key={o.next_scheduled_date || 'empty'}
                        defaultValue={o.next_scheduled_date || ''}
                        onChange={(e) => { if (e.target.value) deferOne(o.id, e.target.value); }}
                        title="Current scheduled date — change it to defer"
                        className="text-xs px-1 py-0.5 border border-gray-300 rounded"
                      />
                    )}
                    {o.lifecycle_stage === 'validation_pending' && isAdmin() && (
                      <button
                        onClick={() => approveAllValidationFlags(o.id, o.order_id_external)}
                        disabled={busyApproveOrderId === o.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                        title="Approve every pending validation flag on this order"
                      >
                        {busyApproveOrderId === o.id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <CheckCircle2 className="w-3 h-3" />}
                        Approve flags
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
        );
      })()}

      <AssignToUserModal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        intakeBatchId={id}
        existingAssigneeIds={(batch?.assignees || []).map((a) => a.user_id)}
        onAssigned={load}
      />

      {/* Bulk-defer modal */}
      {deferBulkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDeferBulkOpen(false)}>
          <div className="bg-white rounded-xl max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 text-sm font-semibold">
              Defer {selected.size} order(s)
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-gray-600">
                Move every pending shipment on the selected orders to a single new date.
              </p>
              <div>
                <div className="text-xs text-gray-600 mb-1">Quick picks (working days, Sunday skipped)</div>
                <div className="flex gap-2">
                  {[3, 7, 14].map((days) => {
                    const iso = addWorkingDaysISO(days);
                    return (
                      <button key={days}
                              onClick={() => setDeferBulkDate(iso)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                                deferBulkDate === iso
                                  ? 'bg-blue-600 border-blue-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}>
                        +{days} working days
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="block">
                <span className="block text-xs text-gray-600 mb-1">Or pick a custom date</span>
                <input type="date" value={deferBulkDate}
                       onChange={(e) => setDeferBulkDate(e.target.value)}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDeferBulkOpen(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={bulkDefer}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                  Defer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
